import { Currency, CurrencyCode } from 'APIs/fragments';
import { JournalEntry } from 'APIs/journals/journal.types';
import { JournalEntriesQueryParams } from 'APIs/journals/journal.types';
import { addJournalEntry, getJournalEntries } from 'APIs/journals/journals';
import { QueryParams } from 'APIs/types';
import { AmountFilterValue } from 'Components/Common/Filters/AmountFilter';
import { DateFilterQueryInput, DateFilterValue, toInstantFilterInput } from 'Components/Common/Filters/DateFilter';
import FilterComponent from 'Components/Common/Filters/FilterComponent';
import { FilterOption } from 'Components/Common/Filters/types';
import Message from 'Components/Message';
import Modal from 'Components/Modal/Modal';
import Pagination from 'Components/Pagination/Pagination';
import Spinner from 'Components/Spinner/Spinner';
import { journalEntryTypes, journaleEntryCategories } from 'Constants/Constants';
import { H2, Label, LpBox, LpDetails, Row } from 'Constants/styles';
import { DateTime } from 'luxon';
import { FormEvent, FunctionComponent, MouseEvent, ReactElement, useCallback, useEffect, useState } from 'react';
import { MdFilterList, MdRefresh } from 'react-icons/md';
import { useSelector } from 'react-redux';
import { formatAmount, formatCurrency, minorCurrencyUnits } from 'utils/currency';
import getErrorMessage from 'utils/getErrorMessage';

import { EditBtn, FilterBtn, FormControl, FormField, PaginationRow, SpinnerWrapper, TextInput } from './styles';

type Props = {
  merchantId: string;
};

const Journal = ({ merchantId }: Props): ReactElement => {
  const RefreshIcon = MdRefresh as FunctionComponent;
  const FilterListIcon = MdFilterList as FunctionComponent;

  const { apiAprilBaseUri } = useSelector((state: ReduxStateType) => ({
    apiAprilBaseUri: state.apiAprilBaseUri,
  }));

  const initFilterOptions: FilterOption[] = [
    {
      option_id: 'amount',
      checked: false,
      value: null,
      label: 'Amount',
      optionType: 'amount',
      optionConfig: {
        conditions: ['is_equal_to'],
      },
    },
    {
      option_id: 'createdOn',
      checked: false,
      value: null,
      label: 'Created on',
      optionType: 'date',
      optionConfig: {
        conditions: ['is_in_the_last', 'is_equal_to', 'is_between', 'is_after', 'is_before'],
      },
    },
    {
      option_id: 'entryType',
      checked: false,
      value: null,
      label: 'Entry type',
      optionType: 'select',
      optionConfig: {
        options: journalEntryTypes,
      },
    },
    {
      option_id: 'category',
      checked: false,
      value: null,
      label: 'Category',
      optionType: 'select',
      optionConfig: {
        options: journaleEntryCategories,
      },
    },
  ];

  const [filterPopupOpen, setFilterPopupOpen] = useState<boolean>(false);
  const [journalEntries, setJournalEntries] = useState<Array<JournalEntry>>([]);
  const [newEntryCategory, setNewEntryCategory] = useState<string>('income');
  const [newEntryAmount, setNewEntryAmount] = useState<string>('');
  const [newEntryCurrency, setNewEntryCurrency] = useState<string>('AUD');
  const [newEntryDesc, setNewEntryDesc] = useState<string>('');
  const [filterOptionValues, setFilterOptionValues] = useState<FilterOption[]>(initFilterOptions);
  const [activePage, setActivePage] = useState<number>(1);
  const [pageStartIndex, setPageStartIndex] = useState<number>(1);
  const [pageCount, setPageCount] = useState<number>(1);
  const [filterCount, setFilterCount] = useState<number>(0);

  const [showModal, setShowModal] = useState<boolean>(false);
  const [modalIsLoading, setModalIsLoading] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [modalErrorMsg, setModalErrorMsg] = useState<string>('');

  const getFilterQueryParams = useCallback((filterOptionValues: FilterOption[]): QueryParams => {
    return filterOptionValues.reduce((acc: QueryParams, option) => {
      if (option.checked && option.value !== null) {
        if (option.option_id === 'amount') {
          const value = option.value as AmountFilterValue;
          if (value.option === 'is_equal_to') {
            acc['amount'] = value.amount as string;
          }
        }
        if (option.option_id === 'createdOn') {
          const dateValue: DateFilterQueryInput | null = toInstantFilterInput(option.value as DateFilterValue);

          if (dateValue) {
            if ('gt' in dateValue) {
              acc['createdFromInclusive'] = dateValue.gt as string;
            }
            if ('gte' in dateValue) {
              acc['createdFromInclusive'] = dateValue.gte as string;
            }
            if ('lt' in dateValue) {
              acc['createdToExclusive'] = dateValue.lt as string;
            }
            if ('lte' in dateValue) {
              acc['createdToExclusive'] = dateValue.lte as string;
            }
          }
        }
        if (option.option_id === 'entryType') {
          acc['entryType'] = option.value as string;
        }
        if (option.option_id === 'category') {
          acc['category'] = option.value as string;
        }
      }
      return acc;
    }, {});
  }, []);

  const fetchJournalEntries = useCallback(async () => {
    if (!apiAprilBaseUri || !merchantId) {
      return;
    }

    try {
      setIsLoading(true);

      const qParams: QueryParams = getFilterQueryParams(filterOptionValues);

      const today = new Date();
      const pastDate = new Date(today);
      pastDate.setMonth(today.getMonth() - 3);

      if (!qParams.createdFromInclusive) qParams.createdFromInclusive = pastDate.toISOString();
      if (!qParams.createdToExclusive) qParams.createdToExclusive = today.toISOString();

      const resp = await getJournalEntries(apiAprilBaseUri, {
        ...(qParams as JournalEntriesQueryParams),
        merchantId,
        page: activePage,
        sort: '-createdAt',
      });
      setJournalEntries(resp.items);
      setPageCount(resp.totalPages);
    } catch (e) {
      setModalErrorMsg(getErrorMessage(e));
    } finally {
      setIsLoading(false);
    }
  }, [activePage, apiAprilBaseUri, filterOptionValues, getFilterQueryParams, merchantId]);

  const handleAddEntry = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      try {
        const entryAmount = Number(newEntryAmount.replace(/[^0-9.-]/g, ''));
        if (newEntryCategory !== '' && entryAmount !== 0) {
          const confirmed = window.confirm(
            `Are you sure you want to add ${formatCurrency(
              entryAmount,
              newEntryCurrency as CurrencyCode,
            )} (${newEntryCategory}) entry to the journal?`,
          );
          if (!confirmed) {
            return;
          }
          setModalIsLoading(true);
          const payload = {
            MerchantManualTopUp: {
              merchantId,
              amount: {
                minorCurrencyUnits: minorCurrencyUnits(entryAmount),
                currency: newEntryCurrency as CurrencyCode,
              } as Currency,
              category: newEntryCategory as 'income' | 'fee',
              description: newEntryDesc,
            },
          };
          await addJournalEntry(apiAprilBaseUri, payload);
          setNewEntryCategory('income');
          setNewEntryAmount('');
          setNewEntryDesc('');
          fetchJournalEntries();
          setShowModal(false);
        }
      } catch (e) {
        setModalErrorMsg(getErrorMessage(e));
      } finally {
        setModalIsLoading(false);
      }
    },
    [
      apiAprilBaseUri,
      fetchJournalEntries,
      merchantId,
      newEntryAmount,
      newEntryCategory,
      newEntryCurrency,
      newEntryDesc,
    ],
  );

  const toggleModal = (e: MouseEvent<HTMLButtonElement>): void => {
    e.preventDefault();
    setShowModal(true);
    setModalIsLoading(false);
    setModalErrorMsg('');
  };

  const handleCloseModalMsg = (e: MouseEvent<HTMLButtonElement>): void => {
    e.preventDefault();
    setModalErrorMsg('');
  };

  const handleCancel = (e: MouseEvent<HTMLButtonElement>): void => {
    e.preventDefault();
    setShowModal(false);
  };

  useEffect(() => {
    fetchJournalEntries();
  }, [fetchJournalEntries, activePage]);

  return (
    <LpBox>
      <Row>
        <div className="col-sm-7">
          <H2 className="mb-0">Journal</H2>
        </div>
        <div className="col-sm-5 text-right">
          <EditBtn onClick={toggleModal} data-testid="manual-topup-btn">
            Manual topup
          </EditBtn>
        </div>
      </Row>
      {isLoading && (
        <SpinnerWrapper>
          <Spinner color="#0016D1" />
        </SpinnerWrapper>
      )}
      {!isLoading && (
        <div
          className="filter-row position-relative"
          style={{
            position: 'relative',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-end',
            paddingRight: '35px',
          }}
        >
          <FilterBtn className="lp-w-full" onClick={() => fetchJournalEntries()}>
            Refresh <RefreshIcon />
          </FilterBtn>
          <FilterBtn className="lp-w-full" onClick={() => setFilterPopupOpen((prev) => !prev)}>
            Filter {filterCount > 0 && `(${filterCount})`} <FilterListIcon />
          </FilterBtn>
          {filterPopupOpen && (
            <FilterComponent
              filterOptionValues={filterOptionValues}
              initFilterOptionValues={initFilterOptions}
              setFilterOptionValues={setFilterOptionValues}
              handlePageChange={setActivePage}
              handleFilterCount={setFilterCount}
              toggleFilter={() => setFilterPopupOpen(false)}
            />
          )}
        </div>
      )}
      {!isLoading && journalEntries.length === 0 && (
        <LpDetails>
          <p className="text-center">No journal entries found.</p>
        </LpDetails>
      )}
      {!isLoading && journalEntries.length > 0 && (
        <>
          <LpDetails>
            <Row className="pt-4">
              <div className="col-xl-2">
                <Label>Entry ID</Label>
              </div>
              <div className="col-sm-3">
                <Label>Owner Type (Entry type)</Label>
              </div>
              <div className="col-sm-3">
                <Label>Description</Label>
              </div>
              <div className="col-xl-2" style={{ textAlign: 'right', paddingRight: '20px' }}>
                <Label>Debit/Credit</Label>
              </div>
              <div className="col-xl-2">
                <Label>Created at</Label>
              </div>
            </Row>
            {journalEntries.map((entry) => (
              <Row className="pt-4" key={entry.entryId}>
                <div className="col-xl-2">{entry.entryId}</div>
                <div className="col-sm-3">{`${entry.systemOwnerType} (${entry.systemJournalEntryType})`}</div>
                <div className="col-sm-3">{`${entry.description || ''}`}</div>
                <div className="col-xl-2" style={{ textAlign: 'right', paddingRight: '20px' }}>
                  {entry.debit ? '-' : ''}
                  {formatAmount(entry.debit || entry.credit)} {entry.currency}
                </div>
                <div className="col-xl-2">{DateTime.fromISO(entry.createdAt).toFormat('dd/LL/yyyy, HH:mm')}</div>
              </Row>
            ))}
          </LpDetails>
          <PaginationRow>
            <Pagination
              pageCount={pageCount}
              activePage={activePage}
              setActivePage={setActivePage}
              pageStartIndex={pageStartIndex}
              setPageStartIndex={setPageStartIndex}
            />
          </PaginationRow>
        </>
      )}
      {showModal && (
        <Modal
          title="New entry"
          cancelBtnText="Cancel"
          confirmBtnText="Add"
          handleCancel={handleCancel}
          handleSubmit={handleAddEntry}
          disableConfirmBtn={modalIsLoading}
          isLoading={modalIsLoading}
          testId={'journal-entry-modal'}
        >
          {modalErrorMsg.length > 0 && (
            <Message
              className="mt-2 mb-0"
              success={false}
              description={modalErrorMsg}
              handleClose={handleCloseModalMsg}
            />
          )}
          <LpDetails className="pt-2 pb-0">
            <div className="form-item">
              <div className="form-field">
                <div className="pt-2">
                  <Label>Category</Label>
                  <FormControl
                    name="newEntryCategory"
                    value={newEntryCategory}
                    onChange={(e) => setNewEntryCategory(e.target.value)}
                    data-testid="journal-entry-modal-category-select"
                  >
                    <option key="income" value="income">
                      Income
                    </option>
                    <option key="fees" value="fees">
                      Fees
                    </option>
                  </FormControl>
                </div>
                <div className="pt-2">
                  <Label>Currency</Label>
                  <FormControl
                    name="newEntryCurrency"
                    value={newEntryCurrency}
                    onChange={(e) => setNewEntryCurrency(e.target.value)}
                    data-testid="journal-entry-modal-currency-select"
                  >
                    <option key="AUD" value="AUD">
                      AUD
                    </option>
                    <option key="NZD" value="NZD">
                      NZD
                    </option>
                  </FormControl>
                </div>
                <div className="pt-2">
                  <Label>Amount</Label>
                  <FormField>
                    <TextInput
                      type="text"
                      name="newEntryAmount"
                      value={newEntryAmount}
                      required
                      onChange={(e) => setNewEntryAmount(e.target.value)}
                      data-testid="journal-entry-modal-amount-input"
                    />
                  </FormField>
                </div>
                <div className="pt-2">
                  <Label>Description</Label>
                  <FormField>
                    <TextInput
                      type="text"
                      name="newEntryDesc"
                      value={newEntryDesc}
                      onChange={(e) => setNewEntryDesc(e.target.value)}
                      data-testid="journal-entry-modal-desc-input"
                    />
                  </FormField>
                </div>
              </div>
            </div>
          </LpDetails>
        </Modal>
      )}
    </LpBox>
  );
};

export default Journal;
